import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { deliveryClient } from '../../../lib/connect/clients';
import { useTenantId } from '../_shared/hooks/useTenantId';
function mapOrg(o) {
    return {
        id: o.id,
        name: o.name,
        acronym: o.acronym,
        parentId: o.parentId,
        orgType: o.orgType,
        level: o.level,
        displayPath: o.displayPath
    };
}
function orgLabel(o) {
    return o.acronym ? `${o.name} (${o.acronym})` : o.name;
}
export function formatGovernmentOrgLabel(o) {
    return orgLabel(o);
}
const listBase = {
    includeSystem: true,
    includeUsaspendingOffices: false
};
export function tenantEnumSelectOptions(values) {
    return (values ?? []).map((v)=>({
            value: v.id,
            label: v.displayLabel
        }));
}
/**
 * Values for one tenant enum domain. `businessUnitId` is optional and only
 * meaningful for domains whose values can be BU-scoped (today: role_type) —
 * the server treats it as NULL-or-match, returning the company-wide values
 * PLUS that BU's own, so passing one narrows the list without hiding the
 * global names. Callers with no BU in hand pass nothing and get everything.
 */ export function useTenantEnumValues(domain, businessUnitId) {
    const companyId = useTenantId();
    const bu = businessUnitId ?? '';
    return useQuery({
        // The BU has to be part of the key: the unscoped list and a BU-filtered one
        // are different results, and sharing a key lets whichever resolves first
        // serve the other — a silent, load-order-dependent wrong-options bug. It
        // joins the key only when supplied so the many unscoped callers keep theirs.
        queryKey: bu ? [
            'tenantEnum',
            domain,
            companyId,
            bu
        ] : [
            'tenantEnum',
            domain,
            companyId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listTenantEnumValues({
                companyId,
                domain,
                includeSystem: true,
                businessUnitId: bu
            });
            return resp.values;
        },
        enabled: !!companyId && !!domain,
        staleTime: 60000
    });
}
export function useGovernmentOrganizations(opts) {
    const companyId = useTenantId();
    const parentId = opts?.parentId ?? '';
    const search = (opts?.search ?? '').trim();
    const rootsOnly = opts?.rootsOnly ?? false;
    return useQuery({
        queryKey: [
            'governmentOrg',
            companyId,
            parentId,
            rootsOnly,
            opts?.orgType ?? '',
            search
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listGovernmentOrganizations({
                companyId,
                parentId: search || rootsOnly ? '' : parentId,
                orgType: opts?.orgType ?? '',
                includeSystem: listBase.includeSystem,
                includeUsaspendingOffices: listBase.includeUsaspendingOffices,
                search,
                rootsOnly: search ? false : rootsOnly
            });
            return (resp.organizations ?? []).map(mapOrg);
        },
        enabled: (opts?.enabled ?? true) && !!companyId,
        staleTime: 30000
    });
}
/** Adds a tenant office under a registry org (org administration: admin/internal). */ export function useAddGovernmentOffice() {
    const companyId = useTenantId();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (v)=>{
            const resp = await deliveryClient.upsertGovernmentOrganization({
                companyId,
                name: v.name.trim(),
                acronym: v.acronym.trim(),
                orgType: 'office',
                parentId: v.parent.id,
                level: v.parent.level + 1
            });
            return resp.organization ? mapOrg(resp.organization) : null;
        },
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    'governmentOrg'
                ]
            })
    });
}
/** Full visible catalog for resolving ancestor chains when editing. */ export function useGovernmentOrgCatalog() {
    const q = useGovernmentOrganizations({
        enabled: true
    });
    const byId = useMemo(()=>{
        const m = new Map();
        for (const o of q.data ?? []){
            m.set(o.id, o);
        }
        return m;
    }, [
        q.data
    ]);
    return {
        ...q,
        byId
    };
}
/**
 * The tenant's own registry profile ("the current org"), resolved and
 * stamped server-side. `data` is undefined while loading and null when no
 * own-org resolves — callers treat both as "no autofill".
 *
 * The first call per tenant persists the stamp, so callers gate `enabled`
 * on write capability (the briefing panel passes canEdit) — read-only
 * sessions then never trigger the write-on-read.
 */ export function useOwnCompanyProfile(opts) {
    const companyId = useTenantId();
    return useQuery({
        queryKey: [
            'ownCompanyProfile',
            companyId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.getOwnCompanyProfile({});
            return resp.profile ?? null;
        },
        enabled: !!companyId && (opts?.enabled ?? true),
        staleTime: 300000
    });
}
export function useCompanyProfiles(search) {
    const companyId = useTenantId();
    return useQuery({
        queryKey: [
            'companyProfile',
            companyId,
            search ?? ''
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listCompanyProfiles({
                companyId,
                search: search ?? ''
            });
            return resp.profiles ?? [];
        },
        enabled: !!companyId,
        staleTime: 60000
    });
}
export function useGovernmentOrgAncestorChain(orgId, byId) {
    return useMemo(()=>{
        if (!orgId) {
            return [];
        }
        const chain = [];
        const seen = new Set();
        let cur = byId.get(orgId);
        while(cur && !seen.has(cur.id)){
            seen.add(cur.id);
            chain.unshift(cur);
            cur = cur.parentId ? byId.get(cur.parentId) : undefined;
        }
        return chain;
    }, [
        orgId,
        byId
    ]);
}
